import { useMemo } from 'react'

import dayjs from 'dayjs'

// The deep path, matching `AnalyticsFiltersHeader` — `filters/index.ts` deliberately
// exports only the drawer FILTER components, not this shared chrome module.
import { DATE_RANGE_SHORTCUTS, makeDateRangeShortcutItems } from 'components/input/filters/dateRangeChrome'

/**
 * ── THE HERO'S PERIOD IS FIXED AT ALL TIME, AND IT IS STATED RATHER THAN CHOSEN ────────
 *
 * 🚨 THIS FILE USED TO EXPORT A PICKER. `RoiHeroPeriodSelect` — a `Period: …` trigger and a
 * five-row menu (All time / Last 3 months / Last month / This billing period / Sync with
 * analytics) — rendered in the hero's top-right control group. It is gone, along with the
 * option list, the `RoiHeroPeriodId` union and the synced mode. What remains is the one
 * thing the card still needs: the resolved ALL-TIME window, and the formatters that name it.
 *
 * ## Why the picker went
 *
 * Every option it offered was a variation on a question the hero is not the place to ask.
 * The card divides what Vera returned by what the brand PAID, and payment is cumulative:
 * the honest form of that ratio is the whole of what was paid against the whole of what was
 * returned. Narrowing it to "last month" produces a number that is arithmetically fine and
 * rhetorically useless — a brand does not decide whether Vera is worth it from one month's
 * slice, and the slice moves for reasons (an invoice landing on the 3rd rather than the
 * 28th) that have nothing to do with value delivered.
 *
 * `all_time` was already the DEFAULT, so in practice the control's whole job was to let a
 * reader make the figure worse-founded. A control whose every non-default setting degrades
 * the answer is not offering a choice; it is offering a trap.
 *
 * ## "Sync with analytics" went with it, and it could not have survived alone
 *
 * That option existed to BRIDGE TWO WINDOWS — the page's date range and the hero's own —
 * so a reader who had filtered the page to a quarter could ask the hero the same question.
 * With the hero's window fixed there is no second window to sync to, so the option is not
 * merely unused, it is unstatable. Removing the picker and keeping `synced` would have been
 * incoherent.
 *
 * ## ⚠️ THIS IS HALF OF A PAIR. READ `AnalyticsFiltersHeader.dateRangeControl` TOO
 *
 * On its own, deleting this control would have been a REGRESSION. The hero sits above the
 * page's date picker, and a global-looking date control that does not govern the topmost
 * card is exactly the scope lie this page removed when it demoted the Scans / Certificates
 * toggle out of the header. What makes the fixed All time honest is the OTHER half of the
 * change: the date picker moved DOWN into the toolbar row, below the hero, adjacent to the
 * cards it actually scopes. Its reach now reads as "everything beneath me", which is true,
 * and the hero is visibly outside it.
 *
 * So: do not restore a picker here without also moving the page's date control back above
 * the hero, and do not move the page's date control back above the hero without restoring
 * a picker here. Either one alone reintroduces the defect.
 *
 * ── WHAT SURVIVES, AND WHY IT IS STILL A HOOK ─────────────────────────────────────────
 *
 * The card still fetches its own all-time window server-side, into its own redux slot
 * (`analytics.roiHero`), with its own request. That has NOT changed and must not: the page's
 * `analytics.roi` is keyed to the page's date range, and two differently-keyed fetches into
 * one slot is last-writer-wins — the hero could report a multiple measured over a window it
 * is not displaying. Removing the user-facing control does not merge the two slots. See
 * `RoiHero` and `Actions.GET_ANALYTICS_ROI_HERO`.
 *
 * ── SPEND IS INVOICE-BACKED, WHICH IS WHY "ALL TIME" IS OFFERABLE AT ALL ──────────────
 *
 * Worth keeping, because it is the reason this window is defensible and the reason the
 * caveat below is not a bug. Spend comes from SETTLED STRIPE INVOICES
 * (`IVeraSpend.source === 'stripe_invoices'`) — real cash at the price actually charged.
 * Two consequences:
 *
 *  - Accuracy does NOT degrade with WIDTH. Cash basis is arithmetic over integers; a
 *    ten-year range is exactly as well-founded as a ten-day one. This is what killed the
 *    old objection to an all-time denominator: `getVeraSpend` used to PROJECT spend as
 *    `ceil(rangeDays / 30) × today's plan price`, so an all-time figure was INVENTED — from
 *    a 2020 floor that billed 82 intervals at ~$24.5k for a brand that may have paid for
 *    three months, collapsing the multiple to roughly zero. It produced a NUMBER, not an
 *    error, which is why "All time" was once excluded rather than merely discouraged.
 *
 *  - It degrades with RECENCY instead. Usage in the OPEN billing period has not been
 *    invoiced, so any range touching today understates spend — and understated spend
 *    OVERSTATES the return, which is the flattering direction. `exact` goes false and
 *    `approximation_note` says so. ⚠️ THIS CAVEAT SURVIVES the picker's removal and is not
 *    a reason to reinstate a narrower window: over all time the un-invoiced gap is one
 *    period out of the brand's entire history, i.e. the SMALLEST proportion any window
 *    could give it. The narrow options were worse on this axis, not better — "This billing
 *    period" was the open period entire.
 *
 * ── THE LOWER BOUND IS THE COMPANY'S OWN CREATION DATE ────────────────────────────────
 *
 * NOT `VERA_DATA_FLOOR` (2020-01-01), which is the fabricated floor the old implementation
 * used. The caller passes a real `dataFloor` — see `RoiHero`'s call site for why creation
 * rather than the first paid invoice.
 *
 * ── THE WINDOW IS SELECTED FROM THE SHARED PRESET LIST, NOT DEFINED HERE ──────────────
 *
 * `makeDateRangeShortcutItems` already owns what "All time" means in this app, and the
 * filter drawers render the same list. Defining a second all-time window here would look
 * identical, read identically, and diverge the first time either was adjusted. Selection by
 * `DATE_RANGE_SHORTCUTS` key makes them the same window by construction.
 */

/**
 * The hero's window, resolved to real instants: the company's creation date → now.
 *
 * `null` while the shared factory cannot produce it, which the caller must treat as "not
 * ready" rather than substituting a window of its own — a guessed range would be fetched,
 * displayed, and indistinguishable from a measured one.
 *
 * ⚠️ NOT day-floored. The shared preset already gets the boundaries right; do not "tidy"
 * them downstream.
 */
export const useRoiHeroAllTimeRange = (
  /**
   * Where "All time" STARTS — the company's own `created_at`.
   *
   * ⚠️ PASS A REAL ONE. Omitted, the shared factory falls back to `VERA_DATA_FLOOR`
   * (2020-01-01), and that constant's own note calls itself the "we were not told" case.
   * Under invoice-backed spend a too-early floor no longer corrupts the DENOMINATOR — there
   * are no invoices in years the brand did not exist, so the money figure is unchanged — but
   * it is still wrong in two ways worth avoiding: the card's stated scope would claim a
   * history the brand does not have, and the chart's bucket spine would be padded with
   * dozens of empty months that flatten every real lump into the baseline.
   *
   * Optional rather than required because a floor that has not loaded yet must not block the
   * card; the fallback is the honest degradation, not a design choice.
   */
  dataFloor?: string | Date | null,
): { from: Date; to: Date } | null =>
  useMemo(() => {
    const items = makeDateRangeShortcutItems({ includeAllTime: true, dataFloor })
    const item = items.find(i => i.label === DATE_RANGE_SHORTCUTS.allTime)
    if (!item) return null
    // `getValue` is handed an `isValid` predicate by the picker slot it normally serves;
    // this preset does not consult it, but the signature requires it.
    const [from, to] = item.getValue({ isValid: () => true })
    if (!from?.isValid() || !to?.isValid()) return null
    return { from: from.toDate(), to: to.toDate() }
  }, [
    // A PRIMITIVE. A caller passing a `Date` would otherwise hand a fresh identity to every
    // render and re-resolve the range — which is the object the hero's query params are
    // built from, i.e. a new request per render.
    dataFloor instanceof Date ? dataFloor.getTime() : dataFloor,
  ])

/**
 * A range's own label, for the chart's axis caption — "Jul 4 – Aug 3".
 *
 * Short form deliberately: it appears inside a one-line clamped caption beside other text,
 * where a full `MMM D, YYYY` pair on both ends is ~24 characters of a line that has a
 * sentence to fit.
 */
export const formatRoiHeroRange = (range?: { from: Date; to: Date } | null): string =>
  range ? `${dayjs(range.from).format('MMM D')} – ${dayjs(range.to).format('MMM D')}` : ''

/**
 * The hero's stated scope — "All time · since Mar 4, 2026".
 *
 * ⚠️ A START DATE WITH ITS YEAR, not `formatRoiHeroRange`'s day-month pair, and the
 * difference is not cosmetic. All time routinely spans years, and "Mar 4 – Aug 29" cannot
 * distinguish a five-month window from a seventeen-month one — on the one figure whose whole
 * claim is that it covers everything. The END is left unsaid because it is always now, and
 * a reader does not need to be told today's date.
 *
 * Returns the bare label when the window has not resolved, so the card can always state its
 * scope even while the floor is still loading — the scope is fixed and knowable in advance;
 * only the date behind it is not.
 */
export const formatRoiHeroScope = (range?: { from: Date; to: Date } | null): string =>
  range ? `All time · since ${dayjs(range.from).format('MMM D, YYYY')}` : 'All time'
